import React from 'react';
import { cn } from '../../../lib/cn';
import { Icon } from '../../Icon';
import { Illustration, ILLUSTRATION_NAMES } from '../../../illustrations';

/**
 * Empty, the "there is nothing here" block.
 *
 * Source selectors: `system.css` `.dsk-empty` + `--sm`, `--filtered`,
 * `--error`, `--locked`, `--inline`, and `__icon`, `__title`, `__text`,
 * `__query`, `__detail`, `__actions`.
 *
 * Doctrine enforced:
 * - There are FOUR empty states, not one, and the difference is the way OUT:
 *     default     nothing here yet       the way out is to create
 *     filtered    your filter excluded   the way out is to clear it
 *     error       it failed to load      the way out is to retry
 *     locked      you may not see this   there is no way out, say so
 *   Only the icon halo changes, and it changes for a reason: a filter is state
 *   the USER applied so it is accent, a failure is the error colour tinted and
 *   never a filled red panel, a permission wall is ink because it is the system
 *   holding a line rather than a fault.
 * - `query` echoes the search term back, which is what proves the screen heard
 *   you, and it is never allowed to be the only line: it is a fragment.
 * - `size="sm"` is for a floating layer, where a full-height empty state would
 *   make the panel taller than the content it replaced. `inline` is for a
 *   `Card flush` or a listing list, which brings its own inset.
 *
 * THERE IS NO DEFAULT ILLUSTRATION, and that is a decision rather than an
 * omission. The argument for one is real: an audit of the call sites found
 * whole-surface empty states rendering a bare glyph purely because nobody
 * passed a slug, and a default would have caught every one of them at once.
 * The argument against wins anyway. The artwork does not say "empty", it says
 * WHAT is missing and what the way out is — a broken read, an excluding
 * filter, a bare catalogue and a permission wall are four different pictures
 * and the component cannot tell which it is in. Any default is therefore
 * wrong most of the time, and wrong artwork is worse than none: none reads as
 * a plain state, whereas a cart on a failed payout read tells the user
 * something false and, because it looks finished, nobody ever comes back to
 * fix it. `ConsoleEmpty` can default to `no-orders-yet` precisely because it
 * is NOT this component — it knows its callers are ledger tables. `Empty`
 * knows nothing about its caller. So the missing slugs get fixed at the call
 * sites, and the help this file can honestly give is the warning below.
 *
 * Icon sizing note: `.dsk-empty__icon` sets a 26px font-size on the halo, but
 * `<Icon>` carries `.dsk-icon` at 18px and wins on the glyph itself, so the
 * glyph is stepped up explicitly here. `sm` maps to `lg` (20px), which is the
 * halo's own `--sm` size exactly. Pass a ready-made element as `icon` if you
 * need something else.
 */
export const Empty = React.forwardRef(function Empty(
  {
    icon,
    illustration,
    title,
    titleAs: TitleTag = 'p',
    text,
    query,
    detail,
    actions,
    tone = 'default',
    size,
    inline = false,
    children,
    className,
    ...rest
  },
  ref,
) {
  const toneClass = {
    default: null,
    filtered: 'dsk-empty--filtered',
    error: 'dsk-empty--error',
    locked: 'dsk-empty--locked',
  }[tone];

  const small = size === 'sm';

  // THE ILLUSTRATION IS THE TREATMENT. `inline` used to suppress it entirely
  // and fall back to a Phosphor glyph in a tinted disc, which is how a card's
  // ERROR state came to look nothing like the EMPTY state one data-fetch away
  // from it — same panel, same failure to show rows, two different pictures.
  // What `inline` actually means is "this state lives inside a panel and does
  // not own the page", and the honest answer to that is smaller artwork, not
  // different artwork. So it steps the illustration down to `sm` instead.
  //
  // `size="sm"` (a floating layer: a dropdown, a popover) still takes the
  // glyph. There is genuinely no room for artwork in a 240px menu.
  //
  // An illustration WINS over a passed `icon`, because a caller supplying both
  // has almost certainly added the illustration to an existing call and
  // forgotten to remove the icon. Rendering both would stack two pictures.
  const illustrated = Boolean(illustration) && !small;

  // THE SILENT FAILURE. A slug that is not in the registry renders nothing at
  // all: `Illustration` returns null rather than throwing, on the reasonable
  // grounds that a missing decoration should not take a page down. The cost is
  // that a typo looks exactly like a deliberate glyph state, and its only
  // trace is a console warning naming the SLUG — which does not say which of
  // the four empty states on the page asked for it. The title is what a person
  // can search for, so it is warned with here, next to the prop that was
  // wrong. Dev only, and it never changes what renders.
  if (
    process.env.NODE_ENV !== 'production' &&
    illustration &&
    !ILLUSTRATION_NAMES.includes(illustration)
  ) {
    // eslint-disable-next-line no-console
    console.warn(
      `<Empty title="${title}"> asked for illustration "${illustration}", which has no artwork, so it will render none. Known: ${ILLUSTRATION_NAMES.join(', ')}`,
    );
  }

  let glyph = null;
  if (React.isValidElement(icon)) {
    glyph = icon;
  } else if (icon) {
    glyph = <Icon as={icon} size={small ? 'lg' : 'xl'} />;
  }

  return (
    <div
      ref={ref}
      className={cn(
        'dsk-empty',
        toneClass,
        small && 'dsk-empty--sm',
        inline && 'dsk-empty--inline',
        className,
      )}
      {...rest}
    >
      {illustrated ? <Illustration name={illustration} size={inline ? 'xs' : 'md'} /> : null}
      {!illustrated && glyph ? <div className="dsk-empty__icon">{glyph}</div> : null}
      {title ? <TitleTag className="dsk-empty__title">{title}</TitleTag> : null}
      {text || query ? (
        <p className="dsk-empty__text">
          {text}
          {query ? <span className="dsk-empty__query">{query}</span> : null}
        </p>
      ) : null}
      {detail ? <p className="dsk-empty__detail">{detail}</p> : null}
      {children}
      {actions ? <div className="dsk-empty__actions">{actions}</div> : null}
    </div>
  );
});
Empty.displayName = 'Empty';

export default Empty;
